'use client';

import { useSetAtom } from 'jotai';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { confirmPasswordReset, requestPasswordReset } from '@/app/_authAction/password-reset';
import BasicButton from '@/components/basics/BasicButton';
import BasicInput from '@/components/basics/BasicInput';
import ErrorMessages from '@/components/basics/ErrorMessages';
import { toastTypes } from '@/components/basics/Toast';
import FormPassword from '@/components/features/form/FormPassword';
import PasswordPolicyChecklist from '@/components/features/form/PasswordPolicyChecklist';
import { validatePassword } from '@/lib/auth/password-policy';
import toastAtom from '@/stores/atoms/toastAtom';

export default function PasswordResetPage() {
  const router = useRouter();
  const setToast = useSetAtom(toastAtom);
  const searchParams = useSearchParams();
  // 独自リセットフローでは URL パラメータを `token` で受け取る
  const token = searchParams?.get('token');
  const from = searchParams?.get('from');

  const [step, setStep] = useState<'emailConfirm' | 'resetPassword' | 'complete'>(
    !token ? 'emailConfirm' : 'resetPassword',
  );
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [passwordErrorMessages, setPasswordErrorMessages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasTokenError, setHasTokenError] = useState(false);

  const handleSendEmail = async () => {
    setIsLoading(true);
    const result = await requestPasswordReset({ email, origin: window.location.origin });
    if (result.success) {
      localStorage.setItem('resetPassword:email', email);
      setToast({
        text: 'パスワード再設定のメールを送信しました',
        isOpen: true,
        type: toastTypes.success,
      });
    } else {
      setToast({
        text: 'システムエラーが発生しました',
        isOpen: true,
        type: toastTypes.error,
      });
    }
    setIsLoading(false);
  };

  const handleChangePassword = async () => {
    setErrorMessages([]);
    setIsLoading(true);

    try {
      if (!token) {
        setErrorMessages(['エラーが発生しました。メール送信からやり直してください']);
        setHasTokenError(true);
        throw new Error();
      }
      if (newPassword !== confirmNewPassword) {
        setErrorMessages(['入力されたパスワードと確認用パスワードが一致しません。']);
        throw new Error();
      }

      const passwordErrors = validatePassword(newPassword);
      if (passwordErrors.length > 0) {
        setErrorMessages(passwordErrors);
        throw new Error();
      }

      const result = await confirmPasswordReset({ token, newPassword });
      if (result.success) {
        localStorage.removeItem('resetPassword:email');
        setStep('complete');
        setToast({
          text: 'パスワードの再設定を完了しました',
          isOpen: true,
          type: toastTypes.success,
        });
      } else {
        switch (result.errorName) {
          case 'InvalidPasswordException':
            const validationErrors = validatePassword(newPassword);
            setErrorMessages(validationErrors.length > 0 ? validationErrors : ['パスワードの形式が正しくありません']);
            break;
          case 'LimitExceededException':
            setErrorMessages(['リクエストの上限に達しました。しばらくしてから再試行してください。']);
            break;
          case 'TokenExpired':
            setErrorMessages(['リンクの有効期限が切れました。メール送信からやり直してください']);
            setHasTokenError(true);
            break;
          case 'TokenInvalid':
            setErrorMessages(['リンクが無効です。メール送信からやり直してください']);
            setHasTokenError(true);
            break;
          default:
            setErrorMessages(['エラーが発生しました。メール送信からやり直してください']);
            setHasTokenError(true);
        }
        setToast({
          text: 'エラーが発生しました',
          isOpen: true,
          type: toastTypes.error,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickBackEmailConfirm = () => {
    setStep('emailConfirm');
    setHasTokenError(false);
    localStorage.removeItem('resetPassword:email');
  };

  useEffect(() => {
    const email = localStorage.getItem('resetPassword:email');
    if (email) setEmail(email);
  }, []);

  return (
    <div className="flex size-full items-center justify-center">
      <div className="flex flex-col items-center gap-6">
        <Image priority src="/images/header_logo.svg" alt="Kuden Business Logo" width={160} height={20} />
        <h1 className="text-2xl font-bold">パスワード再設定</h1>
        {step === 'emailConfirm' && (
          <>
            <div className="flex flex-col gap-2">
              <label className="font-semibold">メールアドレス</label>
              <BasicInput
                className="w-[300px]"
                type="email"
                placeholder="example@kuden.co.jp"
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <BasicButton className="w-[300px]" color="primary" isLoading={isLoading} onPress={() => handleSendEmail()}>
              送信
            </BasicButton>
            <Link className="text-primary" href={from === 'mypage' ? '/mypage' : '/auth/signin'}>
              <span>{from === 'mypage' ? 'マイページ' : 'ログイン'}</span>
              <span>へ戻る</span>
            </Link>
          </>
        )}
        {step === 'resetPassword' && (
          <>
            <div className="flex flex-col gap-2">
              <label className="font-semibold">新しいパスワード</label>
              <FormPassword
                className="w-[300px]"
                placeholder="パスワードを入力してください"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                errorMessages={passwordErrorMessages}
              />
              <PasswordPolicyChecklist className="w-[300px] max-w-full" password={newPassword} />
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-semibold">新しいパスワード（確認用）</label>
              <FormPassword
                className="w-[300px]"
                placeholder="パスワードを入力してください"
                onChange={e => setConfirmNewPassword(e.target.value)}
              />
            </div>
            {errorMessages.length > 0 && <ErrorMessages errors={errorMessages} size="sm" />}
            {hasTokenError && (
              <BasicButton className="w-[300px]" color="secondary" onPress={handleClickBackEmailConfirm}>
                メール送信に戻る
              </BasicButton>
            )}
            <BasicButton
              className="w-[300px]"
              color="primary"
              isLoading={isLoading}
              onPress={() => handleChangePassword()}
            >
              パスワードを変更する
            </BasicButton>
          </>
        )}
        {step === 'complete' && (
          <>
            <p>パスワードの再設定が完了しました</p>
            <BasicButton
              className="w-[300px]"
              color="primary"
              onPress={() => router.push(`/auth/signin?callbackUrl=${window.location.origin}/find-learning`)}
            >
              ログイン
            </BasicButton>
          </>
        )}
      </div>
    </div>
  );
}
